import { javaIsBlank } from '../javaText.ts';
import { formatCalendarDate, type CalendarSystem } from './calendarEngine.ts';

/**
 * A point in a realm's history, as the desktop's `domain.ChronologyDate`: a label written
 * any way ("Year 17, Frostmoon 3", "450 BCE"), a signed sort key that puts dates in order
 * (the same number as a calendar's absolute day), and whether it's uncertain.
 *
 * Sort keys are 64-bit in the realm, so they're read exactly, as `bigint` (decision 2 of
 * docs/PARITY-STEP-3-PLAN.md); a key typed on the web is kept within ±(2^53 − 1), where a
 * browser's numbers are exact, so it can also be shown in a calendar.
 */
export interface ChronologyDate {
  readonly sortKey: bigint;
  readonly label: string;
  readonly uncertain: boolean;
}

export const MAX_SORT_KEY = BigInt(Number.MAX_SAFE_INTEGER);

export class SortKeyError extends Error {}

/** The label, or the sort key when there's no label (`ChronologyDate.display`). */
export function displayDate(date: ChronologyDate): string {
  return javaIsBlank(date.label) ? date.sortKey.toString() : date.label;
}

/** As the desktop lists a date: its display, marked when uncertain. */
export function describeDate(date: ChronologyDate): string {
  return displayDate(date) + (date.uncertain ? ' (uncertain)' : '');
}

/** A start, or a start and end, as the desktop's Chronology writes them. */
export function describeRange(start: ChronologyDate, end: ChronologyDate | null): string {
  return end === null ? describeDate(start) : `${describeDate(start)} – ${describeDate(end)}`;
}

/** The date in a calendar, or null when its key is beyond what the web can work out exactly. */
export function inCalendar(calendar: CalendarSystem, date: ChronologyDate): string | null {
  if (date.sortKey > MAX_SORT_KEY || date.sortKey < -MAX_SORT_KEY) return null;
  return formatCalendarDate(calendar, Number(date.sortKey));
}

/** Orders by sort key, as the desktop's queries do. */
export function compareDates(a: ChronologyDate, b: ChronologyDate): number {
  return a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0;
}

/**
 * A typed sort key: a whole number, optionally signed, within ±(2^53 − 1).
 * @throws SortKeyError with a message to show beside the field.
 */
export function parseSortKey(typed: string): bigint {
  const trimmed = typed.trim();
  if (!/^[+-]?\d+$/.test(trimmed)) throw new SortKeyError('The sort key must be a whole number, such as 2042 or -450.');
  const key = BigInt(trimmed);
  if (key > MAX_SORT_KEY || key < -MAX_SORT_KEY) {
    throw new SortKeyError(`The sort key must be between -${MAX_SORT_KEY.toLocaleString('en')} and ${MAX_SORT_KEY.toLocaleString('en')}.`);
  }
  return key;
}
